import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Blade from '@/components/ui/Blade/Blade';
import Button from '@/components/ui/Button';
import { managementSupportedHref } from '@/pages/Management/SupportedsManagement/IsraeliIdChangeImpact/israeliIdChangeBlocker';
import styles from '../FundsManagement/FundBlade/FundBlade.module.css';
import { CoordinatorToReview, fundLabel, nameOf, recordNameOf } from './reviewRows';

const PREFIX = 'leadCoordinatorsReview';

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className={styles.personMeta}>{label}</span>
    <span className="body-sm-semibold break-words">{children}</span>
  </div>
);

/** An action with the line that says what it does - the office meets four of them here. */
const Action = ({ button, hint }: { button: ReactNode; hint: string }) => (
  <div className="flex flex-col items-start gap-1">
    {button}
    <span className={styles.personMeta}>{hint}</span>
  </div>
);

interface LeadCoordinatorReviewBladeProps {
  row: CoordinatorToReview;
  onClose: () => void;
  onSame: (row: CoordinatorToReview) => void;
  onOtherId: (row: CoordinatorToReview) => void;
  onCancel: (row: CoordinatorToReview) => void;
}

/**
 * A coordinator to review, whole (HAT-10552, 3.11.2026): the account, the record holding its number,
 * every project where the appointment waits, and the four ways to settle it - the same person, the
 * account's number is wrong, the record's number is wrong (fixed on the supported's card, with its
 * impact check; the appointment then takes effect by itself), or not appointing them at all.
 */
const LeadCoordinatorReviewBlade = ({ row, onClose, onSame, onOtherId, onCancel }: LeadCoordinatorReviewBladeProps) => {
  const { t } = useTranslation();
  const hasRecord = row.recordContactId != null;
  // Only a supported has a card to open; a record that only represents a fund is not one.
  const recordCard = hasRecord && row.recordSupportedInFunds.length > 0 ? managementSupportedHref(row.recordContactId!) : null;

  const headerTop = <span className="body-lr-semibold text-(--font-body-4)">{nameOf(row)}</span>;

  return (
    <Blade handleXclick={onClose} headerTop={headerTop} headerBottom={<></>} width="560px">
      <div className={`${styles.content} custom-scrollbar px-6 pb-6`}>
        <section className="flex flex-col gap-3 pb-4">
          {hasRecord && (
            <Action
              button={
                <Button variant="primary" onClick={() => onSame(row)}>
                  {t(`${PREFIX}.same`)}
                </Button>
              }
              hint={t(`${PREFIX}.sameHint`)}
            />
          )}
          <Action
            button={
              <Button variant="outline" onClick={() => onOtherId(row)}>
                {t(`${PREFIX}.otherIdLong`)}
              </Button>
            }
            hint={t(`${PREFIX}.otherIdHint`)}
          />
          {recordCard && (
            <Action
              button={
                <a className={styles.rowEditBtn} href={recordCard} target="_blank" rel="noopener noreferrer">
                  {t(`${PREFIX}.fixRecord`)} ↗
                </a>
              }
              hint={t(`${PREFIX}.fixRecordHint`)}
            />
          )}
          <Action
            button={
              <Button variant="outline" onClick={() => onCancel(row)}>
                {t(`${PREFIX}.cancelAppointment`)}
              </Button>
            }
            hint={t(`${PREFIX}.cancelAppointmentHint`)}
          />
        </section>

        <div className={styles.line} />
        <section className="flex flex-col gap-4">
          <h3 className={styles.sectionTitle}>{t(`${PREFIX}.coordinator`)}</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t(`${PREFIX}.fields.name`)}>{nameOf(row)}</Field>
            <Field label={t(`${PREFIX}.fields.israeliId`)}>{row.israeliId ? <bdi>{row.israeliId}</bdi> : '-'}</Field>
            <Field label={t(`${PREFIX}.fields.email`)}>
              <bdi dir="ltr">{row.email}</bdi>
            </Field>
          </div>
        </section>

        <div className={styles.line} />
        <section className="flex flex-col gap-4">
          <h3 className={styles.sectionTitle}>{t(`${PREFIX}.record`)}</h3>
          {hasRecord ? (
            <div className="grid grid-cols-2 gap-4">
              <Field label={t(`${PREFIX}.fields.name`)}>{recordNameOf(row) || '-'}</Field>
              <Field label={t(`${PREFIX}.fields.email`)}>{row.recordEmail ? <bdi dir="ltr">{row.recordEmail}</bdi> : t(`${PREFIX}.noEmail`)}</Field>
              {row.recordSupportedInFunds.length > 0 && <Field label={t(`${PREFIX}.fields.supportedIn`)}>{row.recordSupportedInFunds.join(', ')}</Field>}
              {row.recordRepresentsFunds.length > 0 && <Field label={t(`${PREFIX}.fields.representsIn`)}>{row.recordRepresentsFunds.join(', ')}</Field>}
            </div>
          ) : (
            <span className={styles.personMeta}>{t(`${PREFIX}.noRecord`)}</span>
          )}
        </section>

        <div className={styles.line} />
        <section className="flex flex-col gap-4">
          <h3 className={styles.sectionTitle}>{t(`${PREFIX}.waitingIn`)}</h3>
          <ul className="flex flex-col gap-1">
            {row.funds.map((fund) => (
              <li key={fund.fundId} className="body-sm-semibold">
                {fundLabel(fund)}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Blade>
  );
};

export default LeadCoordinatorReviewBlade;
